import { Injectable } from '@angular/core';
import { InstanaLoggingBootstrapperConfig } from './instana-logging-bootstrapper.config';

/**
 * Service for managing Instana EUM configuration and console fallback.
 */
@Injectable()
export class InstanaConfigService {
  /**
   * Applies the configuration using the global Instana API (ineum).
   * It's safe to call this even if the full agent hasn't loaded,
   * as the 'ineum' function queues commands.
   */
  applyConfig(
    config: InstanaLoggingBootstrapperConfig,
    onError?: (error: Error) => void
  ): void {
    const { key, reportingUrl, allowedOrigins } = config;

    const ineum = (window as any).ineum;
    if (ineum) {
      try {
        // Set the key before the reporting URL. A live agent must never hold a
        // reporting endpoint without a key, or it sends a key-less beacon.
        ineum('key', key);
        ineum('reportingUrl', reportingUrl);
        ineum('allowedOrigins', allowedOrigins);
        ineum('page', 'loading-screen');
        ineum('user', 'unknown');
      } catch (error: unknown) {
        const errorObj =
          error instanceof Error ? error : new Error(String(error));
        if (onError) {
          onError(errorObj);
        }
      }
    } else {
      const error = new Error('Instana EUM API (ineum) not available.');
      if (onError) {
        onError(error);
      }
    }
  }

  /**
   * Sets up a console.log fallback for the ineum function when Instana script fails to load.
   *
   * This method replaces the Instana EUM queue function with a console logging implementation
   * that mimics the Instana API. This ensures:
   * - The app doesn't break if Instana is unavailable
   * - All Instana API calls are logged to console for debugging
   * - The application continues to function normally
   *
   * The fallback handles all Instana API commands:
   * - reportError: Logs to console.error
   * - meta: Logs metadata to console.log
   * - Other commands: Logs to console.log with command name
   */
  setupConsoleFallback(): void {
    const w = window as any;

    // Keep the commands the real agent has not drained yet. A load timeout
    // leaves the <script> tag in the DOM, so the agent can still arrive and
    // read this queue. Discarding it would boot the agent without a key, which
    // is the key-less beacon this fallback must not cause.
    // Read the queue BEFORE the assignment below replaces w.ineum: the new
    // function object carries no `q` of its own.
    const queuedCommands: any[] = w.ineum?.q ?? [];

    // Replace the queue function (or create new) with console fallback
    w.ineum = function (...args: any[]) {
      const [command, ...rest] = args;

      if (command === 'reportError') {
        const [message, payload] = rest;
        console.error(
          '[Instana Fallback] reportError:',
          message,
          payload || ''
        );
      } else if (command === 'meta') {
        const [key, value] = rest;
        console.log(`[Instana Fallback] meta.${key}:`, value);
      } else {
        console.log(
          `[Instana Fallback] ${command}:`,
          rest.length === 1 ? rest[0] : rest
        );
      }
    };

    // Initialize queue-like properties for compatibility
    w.ineum.q = queuedCommands;
    w.ineum.v = 2;
    w.ineum.l = Date.now();
  }
}
